import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActionMenu, Icon } from '../../../../components';
import { Trigger as MenuTrigger } from '../../../../components/ActionMenu/ActionMenu.styles';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { useReducedMotion } from '@/nova/hooks/useReducedMotion';
import { SORTABLE_ANIM } from '@/nova/theme/tokens';
import { useSortableRows, type SortableRowsApi } from '../../../blog-editor/hooks/useSortableRows';
import { isOnScreen, SCREEN_CHROME, type ListPlacement, type ScreenInsets } from '../../game-list-geometry';
import { gameMoveItems } from '../../game-move-menu';
import { sendAway } from '../../send-away';
import { gameKey, gameThumbUrl, providerLabelOf, type SiteGame } from '../../site-games';
import { DragDemo, type DragGrip } from '../GameLists/dragDemo';
import { GameThumb } from '../GameThumb';
import {
  FirstGamesMark,
  List,
  RowsFrame,
  Row,
  Handle,
  Position,
  Thumb,
  Img,
  Titles,
  Name,
  Meta,
  Kebab,
} from './GameListRows.styles';

export type GameListRowsProps = {
  /** The rendered window of the list, in order: the head of the full list. */
  games: SiteGame[];
  /** The full list's length, so "Move to bottom" lands after the games that are not rendered yet. */
  total: number;
  /** Provider id → display name, from the site's provider source. */
  providerNames: Record<string, string>;
  /** Move one game to `to`, an index in the FULL list. */
  onMove: (id: string, to: number) => void;
  /** The ids a live filter matches (the others fade back), or null while nothing is filtered. */
  matches?: ReadonlySet<string> | null;
  /** The match the filter's steps are on. */
  currentMatch?: string | null;
  /** A row's menu opened. */
  onMenuOpen?: () => void;
  /**
   * The band of the screen a row's menu opens within and a moved row counts as on screen in, measured when
   * asked (clear of fixed chrome, or of the dialog around the list). Keep it stable: the rows are memoized.
   */
  band?: () => ScreenInsets;
  /** Where the list is shown (a dialog scrolls its own area: the rows' scroll margin and drag edges follow). */
  placement?: ListPlacement;
  /** Games of the list the catalogue no longer holds: each row says so in place of its name. */
  unavailable?: ReadonlySet<string>;
  /** Offers "Remove from list" on the unavailable games' rows. */
  onRemove?: (id: string) => void;
  /** The games are the SLIM catalogue's records, which carry no art: a game with none of its own has it read by id
   *  (see `GameListGridProps.artById`). On, the rows need the app's redux store. */
  artById?: boolean;
  /** Plays the FTUX's micro drag on the rows again and again while true (see GameLists/dragDemo). */
  demoLoop?: boolean;
};

const NOTHING_UNAVAILABLE: ReadonlySet<string> = new Set();

const PAGE_BAND = (): ScreenInsets => SCREEN_CHROME.phone;
const NO_INSETS: ScreenInsets = { top: 0, bottom: 0 };

/** The FTUX's games piece lights the page's list of games (not the one in a dialog). */
const GAMES_LIST_TARGET: FtuxTargetId = 'games-list';
/** The list's first games, what players meet first (the FTUX's games piece lights these, never the whole list). */
const FIRST_GAMES_TARGET: FtuxTargetId = 'games-list-top';
const FIRST_GAMES = 5;

/** Where the first games stand in the frame: from the first row's top to the last one's foot (px), or none. */
type FirstGamesBox = { top: number; height: number };

/**
 * The first games' box in the list's frame, measured from the rows as laid out (a row's place, never its drag
 * transform) and again as the order or the list's size changes.
 */
const useFirstGamesBox = (listRef: React.RefObject<HTMLElement>, order: string, on: boolean): FirstGamesBox | null => {
  const [box, setBox] = React.useState<FirstGamesBox | null>(null);
  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (!on || !list) return undefined;
    const measure = (): void => {
      const rows = rowsOf(list).slice(0, FIRST_GAMES);
      const first = rows[0];
      const last = rows.at(-1);
      if (!first || !last) {
        setBox(null);
        return;
      }
      const next = { top: first.offsetTop, height: last.offsetTop + last.offsetHeight - first.offsetTop };
      setBox((prev) => (prev && prev.top === next.top && prev.height === next.height ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [listRef, order, on]);
  return box;
};

/** The grip, the row's drag handle: the demo's hand presses it. */
const GRIP = '[data-grip]';

/** A row's corners (GameListRows.styles' Row), which the copy in the demo's hand keeps. */
const ROW_RADIUS_PX = 12;

/** The demo's hand on a row (useSortableRows' long press): it presses the grip and holds it as long as the product
 *  waits before a row lifts, then the row lifts as the product lifts it; a touch or a scroll takes over. */
const ROW_GRIP: DragGrip = {
  at: (slot, box) => {
    const grip = slot.querySelector(GRIP)?.getBoundingClientRect() ?? box;
    return { x: grip.left - box.left + grip.width / 2, y: grip.top - box.top + grip.height / 2 };
  },
  holdS: SORTABLE_ANIM.LONG_PRESS_MS / 1000,
  liftScale: SORTABLE_ANIM.LIFT_SCALE,
  radius: ROW_RADIUS_PX,
  takeover: 'touch',
};

const rowsOf = (list: HTMLElement): HTMLElement[] =>
  Array.from(list.children).filter(
    (node): node is HTMLElement => node instanceof HTMLElement && Boolean(node.dataset.id),
  );

const topsOf = (list: HTMLElement): Map<string, number> =>
  new Map(rowsOf(list).map((el) => [el.dataset.id ?? '', el.getBoundingClientRect().top]));

/** A row's name: the game's, or what happened to it. */
const rowName = (t: TFunction, game: SiteGame, unavailable: boolean): string =>
  unavailable ? t('Unavailable game') : game.name || `#${gameKey(game)}`;

type RowBodyProps = {
  game: SiteGame;
  name: string;
  /** Where the row would land if the running drag ended now (its index otherwise). */
  position: number;
  provider: string;
  unavailable: boolean;
  /** A game with no art of its own has it read by id (see `GameListRowsProps.artById`). */
  artById: boolean;
  /** The grip: the row's drag handle, or its still look on the copy in the demo's hand. */
  grip: React.ReactNode;
  /** The menu: the real one, or its still look on the copy in the demo's hand. */
  menu: React.ReactNode;
};

/** The thumbnail a game's row shows, once its URL is known. */
const thumbImg = (src: string): React.ReactElement => <Img src={src} alt="" loading="lazy" draggable={false} />;

/** A row's insides (grip, position, thumbnail, name, ID + provider, menu): the row itself, and its copy in the demo's
 *  hand. */
const RowBody = ({
  game,
  name,
  position,
  provider,
  unavailable,
  artById,
  grip,
  menu,
}: RowBodyProps): React.ReactElement => {
  const id = gameKey(game);
  const thumb = unavailable ? undefined : gameThumbUrl(game);
  return (
    <>
      {grip}
      <Position text={String(position + 1)} raw />
      <Thumb>
        {thumb ? thumbImg(thumb) : artById && !unavailable ? <GameThumb game={game} render={thumbImg} /> : null}
        {unavailable ? <Icon name="eye-off" size={16} /> : null}
      </Thumb>
      <Titles>
        <Name text={name} raw $muted={unavailable} />
        {unavailable ? (
          <Meta text="No longer in the catalogue · ID {{id}}" placeholders={{ id }} />
        ) : (
          <Meta text="ID {{id}} · {{provider}}" placeholders={{ id, provider }} />
        )}
      </Titles>
      {menu}
    </>
  );
};

/** The grip's and the menu's look on the copy in the demo's hand (no control: the copy is only shown). */
const STILL_GRIP = (
  <Handle as="span" aria-hidden="true" data-grip="">
    <Icon name="drag-handle" size={18} />
  </Handle>
);
const STILL_MENU = (
  <Kebab>
    <MenuTrigger as="span">
      <Icon name="more" size={18} />
    </MenuTrigger>
  </Kebab>
);

type GameRowProps = {
  game: SiteGame;
  index: number;
  total: number;
  /** Where the row would land if the running drag ended now (its index otherwise). */
  position: number;
  shift: number;
  animated: boolean;
  dragged: boolean;
  /** Filtered out: faded back. */
  dimmed: boolean;
  /** The filter's current match. */
  current: boolean;
  provider: string;
  handleProps: SortableRowsApi['handleProps'];
  rowProps: SortableRowsApi['rowProps'];
  onMove: (id: string, to: number) => void;
  onMenuOpen?: () => void;
  band: () => ScreenInsets;
  placement: ListPlacement;
  unavailable: boolean;
  onRemove?: (id: string) => void;
  artById: boolean;
};

/*
 * Memoized: a drag re-renders the list on every pointer move, and only the lifted row (plus the neighbour it
 * passes) actually changes. Measured at 4x CPU slowdown before this, a move cost ~11ms of script with 60 rows
 * and ~50ms with 300 (a list after a few "Show more"). The handle / row prop factories are stable during a drag.
 */
const GameRow = React.memo(function GameRow({
  game,
  index,
  total,
  position,
  shift,
  animated,
  dragged,
  dimmed,
  current,
  provider,
  handleProps,
  rowProps,
  onMove,
  onMenuOpen,
  band,
  placement,
  unavailable,
  onRemove,
  artById,
}: GameRowProps): React.ReactElement {
  const { t } = useTranslation();
  const id = gameKey(game);
  const name = rowName(t, game, unavailable);
  const transform =
    shift !== 0 || dragged
      ? `translateY(${shift}px)${dragged ? ` scale(${SORTABLE_ANIM.LIFT_SCALE})` : ''}`
      : undefined;

  return (
    <Row
      data-id={id}
      aria-label={unavailable ? t('Unavailable game (ID {{id}})', { id }) : name}
      $dragged={dragged}
      $animated={animated}
      $dimmed={dimmed && !dragged}
      $current={current}
      $placement={placement}
      style={transform ? { transform } : undefined}
      {...rowProps(index)}
    >
      <RowBody
        game={game}
        name={name}
        position={position}
        provider={provider}
        unavailable={unavailable}
        artById={artById}
        grip={
          <Handle
            type="button"
            aria-label={t('Reorder {{title}}', { title: name })}
            data-grip=""
            {...handleProps(index)}
          >
            <Icon name="drag-handle" size={18} />
          </Handle>
        }
        menu={
          <Kebab>
            <ActionMenu
              align="end"
              label={t('Move {{name}}', { name })}
              viewportInsets={band}
              onOpenChange={(open) => {
                if (open) onMenuOpen?.();
              }}
              items={gameMoveItems(t, id, index, total, onMove, unavailable ? onRemove : undefined)}
            />
          </Kebab>
        }
      />
    </Row>
  );
});

/**
 * A site game list on a phone: one slim row per game (handle, position, thumbnail, name, ID + provider, and
 * a menu), stacked in the order the site renders them. A row is dragged vertically by its handle, or by
 * pressing and holding anywhere on it, and the page scrolls when it is held near an edge. The menu jumps a
 * game to the top or the bottom of the WHOLE list (past the games not rendered yet) or one step either way,
 * without moving the page: the rows glide, and a row sent off screen drifts off in that direction.
 */
export const GameListRows = ({
  games,
  total,
  providerNames,
  onMove,
  matches = null,
  currentMatch = null,
  onMenuOpen,
  band = PAGE_BAND,
  placement = 'page',
  unavailable = NOTHING_UNAVAILABLE,
  onRemove,
  artById = false,
  demoLoop = false,
}: GameListRowsProps): React.ReactElement => {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const ids = React.useMemo(() => games.map(gameKey), [games]);
  // The micro drag shows an order of its own while it plays, and the game in its hand; the list's order is never
  // touched.
  const [demoOrder, setDemoOrder] = React.useState<string[] | null>(null);
  const [demoLifted, setDemoLifted] = React.useState<string | null>(null);
  const byId = React.useMemo(() => new Map(games.map((game) => [gameKey(game), game])), [games]);
  const shownGames = React.useMemo(
    () =>
      demoOrder
        ? demoOrder.flatMap((id): SiteGame[] => {
            const game = byId.get(id);
            return game ? [game] : [];
          })
        : games,
    [demoOrder, byId, games],
  );
  const keys = React.useMemo(() => shownGames.map(gameKey), [shownGames]);

  const onDrop = React.useCallback(
    (from: number, to: number): void => {
      const id = keys[from];
      if (id !== undefined) onMove(id, to);
    },
    [keys, onMove],
  );
  const { listRef, row, handleProps, rowProps } = useSortableRows({
    keys,
    onMove: onDrop,
    longPress: true,
    autoScroll: placement === 'dialog' ? NO_INSETS : SCREEN_CHROME.phone,
  });

  // A drop commits a position the row is already shown at; a menu move lands in a single render instead,
  // so the rows remember where they were and glide from there (FLIP) rather than jump. When the row's new
  // place is off screen (or not rendered), a copy of it drifts off that way and the row itself does not glide.
  const glideFrom = React.useRef<{ tops: Map<string, number>; skip: string | null; ms: number } | null>(null);
  const moveFromMenu = React.useCallback(
    (id: string, to: number): void => {
      const list = listRef.current;
      if (list && !reducedMotion) {
        const rows = rowsOf(list);
        const from = rows.findIndex((el) => el.dataset.id === id);
        const destination = rows[to];
        const offScreen = !destination || !isOnScreen(destination, band());
        const leaving = from !== -1 && to !== from && offScreen;
        if (leaving) sendAway(rows[from], to < from ? 'up' : 'down');
        glideFrom.current = { tops: topsOf(list), skip: leaving ? id : null, ms: SORTABLE_ANIM.SHIFT_MS };
      }
      onMove(id, to);
    },
    [listRef, onMove, reducedMotion, band],
  );
  // The micro drag's steps glide at the desktop demo's pace, the row in its hand left out (it jumps, as a dragged one
  // does).
  const captureDemoLayout = React.useCallback(
    (skipId?: string): void => {
      const list = listRef.current;
      if (list && !reducedMotion) {
        glideFrom.current = { tops: topsOf(list), skip: skipId ?? null, ms: SORTABLE_ANIM.SHIFT_SLOW_MS };
      }
    },
    [listRef, reducedMotion],
  );

  const order = keys.join(' ');
  React.useLayoutEffect(() => {
    const before = glideFrom.current;
    glideFrom.current = null;
    const list = listRef.current;
    if (!before || !list) return;
    for (const el of rowsOf(list)) {
      if (el.dataset.id === before.skip) continue;
      const was = before.tops.get(el.dataset.id ?? '');
      if (was === undefined) continue;
      const dy = was - el.getBoundingClientRect().top;
      if (Math.abs(dy) < 1) continue;
      el.animate([{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }], {
        duration: before.ms,
        easing: SORTABLE_ANIM.EASE,
      });
    }
  }, [order, listRef]);

  const firstGames = useFirstGamesBox(listRef, order, placement === 'page');
  return (
    <>
      <RowsFrame>
        <List
          ref={listRef}
          aria-label={t('Games in this list')}
          data-ftux={placement === 'page' ? GAMES_LIST_TARGET : undefined}
        >
          {shownGames.map((game, index) => {
            const state = row(index);
            return (
              <GameRow
                key={keys[index]}
                game={game}
                index={index}
                total={total}
                position={state.position}
                shift={state.shift}
                animated={state.animated}
                dragged={state.dragged}
                // The row in the demo's hand stays as a dimmed placeholder, as a dragged tile does.
                dimmed={(matches !== null && !matches.has(keys[index])) || demoLifted === keys[index]}
                current={matches !== null && currentMatch === keys[index]}
                provider={providerLabelOf(game, providerNames, t)}
                handleProps={handleProps}
                rowProps={rowProps}
                onMove={moveFromMenu}
                onMenuOpen={onMenuOpen}
                band={band}
                placement={placement}
                unavailable={unavailable.has(keys[index])}
                // Only where it is offered: it changes with every reorder, and every other row's props stay put.
                onRemove={unavailable.has(keys[index]) ? onRemove : undefined}
                artById={artById}
              />
            );
          })}
        </List>
        {firstGames ? <FirstGamesMark aria-hidden="true" data-ftux={FIRST_GAMES_TARGET} style={firstGames} /> : null}
      </RowsFrame>
      {/* The FTUX's micro drag on the rows: the hand presses a row's grip, holds, and carries it to the front. */}
      <DragDemo
        loop={demoLoop}
        ids={ids}
        wrapRef={listRef}
        captureLayout={captureDemoLayout}
        onOrder={setDemoOrder}
        onLift={setDemoLifted}
        renderGhost={(id) => {
          const game = byId.get(id);
          if (!game) return null;
          const gone = unavailable.has(id);
          // Its number is the place it would land if dropped now, as on the row it left.
          return (
            <Row as="div" $dragged={false} $animated={false} $dimmed={false} $current={false} $placement={placement}>
              <RowBody
                game={game}
                name={rowName(t, game, gone)}
                position={keys.indexOf(id)}
                provider={providerLabelOf(game, providerNames, t)}
                unavailable={gone}
                artById={artById}
                grip={STILL_GRIP}
                menu={STILL_MENU}
              />
            </Row>
          );
        }}
        grip={ROW_GRIP}
      />
    </>
  );
};

export default GameListRows;
